/* ==========================================================================
   <seed-mobile-menu> — self-contained mobile navigation drawer.

   Owns: open/close, body scroll-lock, single-open accordion, focus trap,
   and close-on (overlay / link / Escape / resize-to-desktop). Deliberately
   does NOT use Dawn's MenuDrawer so the in-place accordion isn't hijacked by
   its slide-in submenu behaviour. Re-inits cleanly in the theme editor via
   the custom-element lifecycle.
   ========================================================================== */
(function () {
  if (customElements.get('seed-mobile-menu')) return;

  var DESKTOP_QUERY = '(min-width: 990px)';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class SeedMobileMenu extends HTMLElement {
    connectedCallback() {
      this.toggleBtn = this.querySelector('[data-seed-mmenu-open]');
      this.overlay = this.querySelector('[data-seed-mmenu-overlay]');
      this.panel = this.querySelector('.seed-mmenu__panel');
      if (!this.toggleBtn || !this.panel) return;

      this.isOpen = false;
      this._ac = new AbortController();
      var signal = this._ac.signal;

      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onResize = this._onResize.bind(this);

      this.toggleBtn.addEventListener('click', this.open, { signal: signal });

      if (this.overlay) {
        this.overlay.addEventListener('click', this.close, { signal: signal });
      }

      // Accordion toggles
      this.querySelectorAll('[data-seed-mmenu-group-toggle]').forEach(function (btn) {
        btn.addEventListener('click', this._onGroupToggle.bind(this, btn), { signal: signal });
      }, this);

      // Any element flagged to close the drawer (links, close button, logo…)
      this.querySelectorAll('[data-seed-mmenu-close]').forEach(function (el) {
        el.addEventListener('click', this.close, { signal: signal });
      }, this);

      // Portal the overlay + panel onto <body>. The header pill uses
      // backdrop-filter, which makes an ancestor the containing block for
      // position:fixed children — so the drawer must escape it to cover the
      // viewport. Listeners are already bound and travel with the nodes.
      this._portal = document.createElement('div');
      this._portal.className = 'seed-mmenu__portal seed-mmenu';
      if (this.overlay) this._portal.appendChild(this.overlay);
      this._portal.appendChild(this.panel);
      document.body.appendChild(this._portal);

      this._mql = window.matchMedia(DESKTOP_QUERY);
      if (this._mql.addEventListener) {
        this._mql.addEventListener('change', this._onResize);
      } else {
        this._mql.addListener(this._onResize); // Safari < 14
      }
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      if (this._mql) {
        if (this._mql.removeEventListener) {
          this._mql.removeEventListener('change', this._onResize);
        } else {
          this._mql.removeListener(this._onResize);
        }
      }
      document.removeEventListener('keydown', this._onKeydown);
      if (this.isOpen) this._unlockScroll();
      // Remove the portaled drawer so the theme editor doesn't stack copies.
      if (this._portal && this._portal.parentNode) {
        this._portal.parentNode.removeChild(this._portal);
      }
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;

      this.overlay && this.overlay.removeAttribute('hidden');
      this.panel.removeAttribute('hidden');
      // Force a reflow so the transition runs from the off-screen state.
      void this.panel.offsetWidth;

      this._portal.classList.add('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this._lockScroll();

      document.addEventListener('keydown', this._onKeydown);

      var focusTarget = this.panel.querySelector('.seed-mmenu__close') || this.panel;
      focusTarget.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      this._portal.classList.remove('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this._unlockScroll();

      document.removeEventListener('keydown', this._onKeydown);

      var self = this;
      var hide = function () {
        if (self.isOpen) return; // re-opened before the transition finished
        self.panel.setAttribute('hidden', '');
        self.overlay && self.overlay.setAttribute('hidden', '');
      };
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) {
        hide();
      } else {
        var done = false;
        var onEnd = function () {
          if (done) return;
          done = true;
          self.panel.removeEventListener('transitionend', onEnd);
          hide();
        };
        this.panel.addEventListener('transitionend', onEnd);
        setTimeout(onEnd, 520); // fallback if transitionend doesn't fire
      }

      this.toggleBtn.focus({ preventScroll: true });
    }

    _onGroupToggle(btn) {
      var body = document.getElementById(btn.getAttribute('aria-controls'));
      var expanded = btn.getAttribute('aria-expanded') === 'true';

      // Single-open accordion: collapse siblings first. Query the panel (not
      // the host) — the panel has been portaled out of this element.
      this.panel.querySelectorAll('[data-seed-mmenu-group-toggle]').forEach(function (other) {
        if (other === btn) return;
        other.setAttribute('aria-expanded', 'false');
        var otherBody = document.getElementById(other.getAttribute('aria-controls'));
        if (otherBody) otherBody.classList.remove('is-expanded');
      });

      btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (body) body.classList.toggle('is-expanded', !expanded);
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      var focusables = Array.prototype.filter.call(
        this.panel.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null; // visible only
        }
      );
      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;

      if (event.shiftKey && (active === first || !this.panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    _onResize(event) {
      if (event.matches && this.isOpen) this.close(); // reached desktop
    }

    _lockScroll() {
      this._scrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    _unlockScroll() {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  }

  customElements.define('seed-mobile-menu', SeedMobileMenu);
})();
